import React, { FC } from "react";
import { Button, Input, Select } from "@pd/pd-ui-kit";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useGetPartnersForSelect } from "~/entity/Partners/hooks/useGetPartnersForSelect";
import {
  useURLParamsGetter,
  useURLParamsSetter,
} from "~/hooks/useURLParamsSetter";
import {
  getSelectDefaultByValue,
  ISelectFilter,
  joinCsv,
  splitCsv,
} from "~/pages/Directory/shared/filterHelpers";
import {
  createPartnerFilterValue,
  getPartnerFilterValueLabel,
  ILicensesUrlParams,
} from "~/pages/TrainingManagement/model/licensesUrlParams";
import { useLicenseFilterValueOptions } from "~/pages/TrainingManagement/model/useLicenseOrganizations";

import s from "~/pages/Directory/shared/DirectoryFilter.less";

interface ILicensesFilterForm {
  filterBy: ISelectFilter | null;
  filterValues: ISelectFilter[];
  inn: string;
  branchCode: string;
  licenseType: ISelectFilter | null;
  overallState: ISelectFilter | null;
}

const LicensesFilter: FC = () => {
  const { t } = useTranslation();
  const setParams = useURLParamsSetter();
  const urlParams = useURLParamsGetter<ILicensesUrlParams>();

  const filterByOptions = [
    { value: "", label: t("trainingManagement.filter.notSelected") },
    {
      value: "dealer",
      label: t("trainingManagement.filter.filterByOptions.dealer"),
    },
    {
      value: "partner",
      label: t("trainingManagement.filter.filterByOptions.partner"),
    },
    {
      value: "city",
      label: t("trainingManagement.filter.filterByOptions.city"),
    },
    {
      value: "region",
      label: t("trainingManagement.filter.filterByOptions.region"),
    },
    {
      value: "country",
      label: t("trainingManagement.filter.filterByOptions.country"),
    },
    {
      value: "federalDistrict",
      label: t("trainingManagement.filter.filterByOptions.federalDistrict"),
    },
  ];

  const licenseTypeOptions = [
    { value: "all", label: t("trainingManagement.filter.all") },
    {
      value: "basic",
      label: t("trainingManagement.filter.licenseTypeOptions.basic"),
    },
    {
      value: "additional",
      label: t("trainingManagement.filter.licenseTypeOptions.additional"),
    },
  ];

  const overallStateOptions = [
    { value: "all", label: t("trainingManagement.filter.all") },
    {
      value: "green",
      label: t("trainingManagement.filter.overallStateOptions.green"),
    },
    {
      value: "yellow",
      label: t("trainingManagement.filter.overallStateOptions.yellow"),
    },
    {
      value: "red",
      label: t("trainingManagement.filter.overallStateOptions.red"),
    },
  ];

  const buildFormValues = (
    params: ILicensesUrlParams,
  ): ILicensesFilterForm => ({
    filterBy: getSelectDefaultByValue(filterByOptions, params.filterBy),
    filterValues: splitCsv(params.filterValues).map((value) => ({
      value,
      label:
        params.filterBy === "partner"
          ? getPartnerFilterValueLabel(value)
          : value,
    })),
    inn: params.inn || "",
    branchCode: params.branchCode || "",
    licenseType: getSelectDefaultByValue(
      licenseTypeOptions,
      params.licenseType,
    ),
    overallState: getSelectDefaultByValue(
      overallStateOptions,
      params.overallState,
    ),
  });

  const { control, handleSubmit, reset, setValue } =
    useForm<ILicensesFilterForm>({
      defaultValues: buildFormValues(urlParams),
    });

  const filterByValue = useWatch({ control, name: "filterBy" }) as
    | ISelectFilter
    | null
    | undefined;

  const filterValueOptions = useLicenseFilterValueOptions(
    filterByValue?.value || undefined,
  );
  const {
    options: partnerOptions,
    loading: partnerLoading,
    onSearch: onPartnerSearch,
    onOpenChange: onPartnerOpenChange,
  } = useGetPartnersForSelect(filterByValue?.value === "partner");
  const filterValueSelectOptions =
    filterByValue?.value === "partner"
      ? partnerOptions.map(({ value, label }) => ({
          key: value,
          value: createPartnerFilterValue(value, label),
          label,
        }))
      : filterValueOptions;

  const onSubmit = (data: ILicensesFilterForm) => {
    const licenseType = data.licenseType?.value;
    const overallState = data.overallState?.value;

    setParams({
      filterBy: data.filterBy?.value || undefined,
      filterValues: joinCsv(data.filterValues?.map((item) => item.value)),
      inn: data.inn || undefined,
      branchCode: data.branchCode || undefined,
      licenseType: licenseType === "all" ? undefined : licenseType || undefined,
      overallState:
        overallState === "all" ? undefined : overallState || undefined,
      page: "0",
    });
  };

  const cancelForm = () => {
    reset({
      filterBy: filterByOptions[0],
      filterValues: [],
      inn: "",
      branchCode: "",
      licenseType: licenseTypeOptions[0],
      overallState: overallStateOptions[0],
    });
    setParams({
      filterBy: undefined,
      filterValues: undefined,
      inn: undefined,
      branchCode: undefined,
      licenseType: undefined,
      overallState: undefined,
      page: "0",
    });
  };

  return (
    <form className={s.formFilter} onSubmit={handleSubmit(onSubmit)}>
      <div className={s.filterWrapper}>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("trainingManagement.filter.filterByLabel")}
          </span>
          <Controller
            name="filterBy"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={(option) => {
                  setValue("filterValues", []);
                  onChange(option ?? null);
                }}
                options={filterByOptions}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("trainingManagement.filter.valueLabel")}
          </span>
          <Controller
            name="filterValues"
            control={control}
            render={({ onChange, value }) => (
              <Select
                mode="multiple"
                labelInValue
                placeholder={t("trainingManagement.filter.notSelected")}
                value={
                  filterByValue?.value === "partner"
                    ? value.map((item) => {
                        if (item.value !== item.label) {
                          return item;
                        }

                        const option = partnerOptions.find(
                          ({ label }) => label === item.label,
                        );

                        return option
                          ? {
                              ...item,
                              value: createPartnerFilterValue(
                                option.value,
                                option.label,
                              ),
                            }
                          : item;
                      })
                    : value
                }
                onChange={(option) => onChange(option ?? [])}
                options={filterValueSelectOptions}
                disabled={!filterByValue?.value}
                {...(filterByValue?.value === "partner"
                  ? {
                      filterOption: false,
                      loading: partnerLoading,
                      onSearch: onPartnerSearch,
                      onOpenChange: onPartnerOpenChange,
                    }
                  : {})}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>{t("trainingManagement.filter.inn")}</span>
          <Controller
            name="inn"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("trainingManagement.filter.all")}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("trainingManagement.filter.branchCode")}
          </span>
          <Controller
            name="branchCode"
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(event) => onChange(event.target.value)}
                onBlur={onBlur}
                placeholder={t("trainingManagement.filter.all")}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("trainingManagement.filter.licenseTypeLabel")}
          </span>
          <Controller
            name="licenseType"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={(option) => onChange(option ?? null)}
                options={licenseTypeOptions}
              />
            )}
          />
        </div>
        <div className={s.fieldWrapper}>
          <span className={s.label}>
            {t("trainingManagement.filter.overallState")}
          </span>
          <Controller
            name="overallState"
            control={control}
            render={({ onChange, value }) => (
              <Select
                labelInValue
                value={value}
                onChange={(option) => onChange(option ?? null)}
                options={overallStateOptions}
              />
            )}
          />
        </div>
      </div>
      <div className={s.buttons}>
        <Button
          htmlType="button"
          color="secondary"
          variant="text"
          bold={false}
          fontSize="12"
          size="medium"
          onClick={cancelForm}
        >
          {t("trainingManagement.reset")}
        </Button>
        <Button
          htmlType="submit"
          bold
          color="secondary"
          fontSize="12"
          size="medium"
        >
          {t("trainingManagement.show")}
        </Button>
      </div>
    </form>
  );
};

export default LicensesFilter;
